import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { from, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { ProgressData } from 'src/app/modules/shared/dtos/progress-data';
import { ResendUCMResponseModel } from '../../models/resend-ucm-response.model';
import { ResendUcmService } from '../../services/resend-ucm.service';
import { PacUtilityService } from '../../utilities/pac-utility.service';

@Component({
    selector: 'app-resend-ucm',
    templateUrl: './resend-ucm.component.html',
    styleUrls: ['./resend-ucm.component.scss']
})
export class ResendUcmComponent implements OnDestroy {
    public resendUCMForm: FormGroup;
    public pacsResult: {[pac: string]: ResendUCMResponseModel|null} = {};
    public resending: boolean = false;
    private _unsubscribe$: Subject<void> = new Subject<void>();
    private _progress$: Subject<ProgressData> = new Subject<ProgressData>();

    public constructor(
        private fb: FormBuilder,
        private resendUCMService: ResendUcmService,
        private messageService: MessageService
    ) {
        this.resendUCMForm = this.fb.group({
            pacsControl: ['', [Validators.required, this.PACValidator]]
        });
    }

    public ngOnDestroy(): void {
        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }

    public onSubmit(form: FormGroup): void {
        this.resending = true;
        this.pacsResult = {};
        const pacsString = form.controls.pacsControl.value;
        const pacs = PacUtilityService.extractPACsFromString(pacsString);

        pacs.forEach(pac => {
            this.pacsResult[pac] = null;
        });

        from(pacs).pipe(
            mergeMap(pac => this.resendUCMService.resend(pac).pipe(
                map(response => {
                    return { ...response, ...{ pac: pac } };
                }),
                catchError((error: HttpErrorResponse) => {
                    return of({ ...error.error, pac: pac });
                }),
                takeUntil(this._unsubscribe$)
            )),
            map(response => new ResendUCMResponseModel(response)),
            takeUntil(this._unsubscribe$)
        ).subscribe(
            (response) => {
                // this.pacsResult[response.pac] = response;
                if (response.responseCode === 0) {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: response.message });
                } else {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'error', summary: 'Fail', detail: response.message });
                }
            });
    }

    private sendProgressDoneSignal(): void {
        this._progress$.next({
            header: '',
            done: true
        });
    }

    /**
     * This is not a complete way to validate PACs the complete check will be done in back-end
     * This is just a sanitation check for checking PAC's length and simple formats
     */
    private PACValidator(c: FormControl): ValidationErrors | null {
        const pacsString = c.value as string;
        const pacs = PacUtilityService.extractPACsFromString(pacsString);
        let error = null;
        if (pacs.length > 10) {
            error = {
                maxPacNumbers: {
                    valid: false
                }
            };
            return error;
        }
        pacs
            .forEach((pac: string) => {
                if (c.value !== '') {
                    if (!PacUtilityService.validatePacFormat(pac) || pac.length <= 0) {
                        error = {
                            invalidPac: {
                                valid: false
                            }
                        };
                    }
                }
            });
        return error;
    }
}
